'use client';

import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import {
  useGetListsQuery,
  useUpdateListMutation,
} from 'src/api/redux/lists/listsApi';
import { useLogging } from 'src/context/logging';
import { useForm, SubmitHandler } from 'react-hook-form';
import { UserSelector } from 'src/components';
import { Dialog, PulseButton } from 'src/components/PulseUI';

interface ReassignListDialogProps {
  open: boolean;
  onClose: () => void;
  listId: number;
}

const ReassignListDialog: React.FC<ReassignListDialogProps> = ({
  open,
  onClose,
  listId,
}) => {
  const { data: providerLists } = useGetListsQuery({});
  const [updateList, { isLoading }] = useUpdateListMutation();
  const log = useLogging();
  const {
    handleSubmit,
    control,
    setError,
    reset,
    formState: { isValid, isDirty },
  } = useForm();
  const list = providerLists?.results?.find(({ id }) => id === listId);

  const onSubmit: SubmitHandler<{ newOwner: number }> = async (data) => {
    const { newOwner } = data;

    if (!newOwner) {
      setError('newOwner', {
        message: 'A new owner must be selected to re-assign.',
      });
      return;
    }

    try {
      await updateList({
        listId: listId.toString(),
        ownerId: newOwner.toString(),
      }).unwrap();
      log.event('List re-assigned', {
        id: listId,
        name: list?.name || 'Unknown',
      });
      reset({ newOwner: null });
      onClose();
    } catch (err: any) {
      log.exception('Error re-assigning list');
    }
  };

  return (
    <Dialog
      isOpen={open}
      onClose={() => {
        reset({ newOwner: null });
        onClose();
      }}
      size="md"
      className="!h-auto"
      aria-labelledby="reassign-list-dialog-title"
      data-testid="reassign-list-dialog"
    >
      <Dialog.Header>
        <h2
          id="reassign-list-dialog-title"
          className="text-lg font-semibold text-neutral-600"
        >
          <Icon icon={faUser} className="mr-2" aria-hidden="true" />
          {`Assign ${list?.name || 'List'}`}
        </h2>
      </Dialog.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Dialog.Content className="p-6">
          <UserSelector id="newOwner" control={control} />
        </Dialog.Content>
        <Dialog.Actions>
          <PulseButton
            variant="outline"
            color="neutral"
            onClick={() => {
              reset({ newOwner: null });
              onClose();
            }}
            data-testid="reassign-list-dialog-cancel"
          >
            Cancel
          </PulseButton>
          <PulseButton
            type="submit"
            variant="solid"
            color="primary"
            loading={isLoading}
            disabled={!isValid || !isDirty}
            data-testid="reassign-list-dialog-assign"
          >
            Assign
          </PulseButton>
        </Dialog.Actions>
      </form>
    </Dialog>
  );
};

export default ReassignListDialog;
